import React, { useCallback, useMemo, useState } from "react";
import { Box, Button, ButtonBase, IconButton, Tooltip } from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import UndoRounded from "@mui/icons-material/UndoRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import { useForm, useFormState } from "react-final-form";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { APP_TOKENS } from "../../../components/publicForm/publicFormTokens";
import colors from "../../../theme/colors";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import formatAddressObjectFullAddress from "../../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import {
  PROPERTY_ROWS,
  REVIEW_SECTIONS,
  isBlank,
  isConstruction,
  resolveRow,
} from "../reviewSchema";

/**
 * Read-back of everything the applicant entered, editable in place.
 *
 * The review is the last thing they see before signing an attestation covering
 * these answers, so every answer has to be both visible AND correctable here.
 * Sending them back to the step to fix a typo cost them the whole step's
 * scroll position and their place in the review; each row instead opens the
 * SAME Rff* control its step uses, writing to the same react-final-form store.
 * There is no save — the form already has the value the moment it changes — so
 * the only per-row actions are "done editing" and "undo back to what I had".
 *
 * Which rows exist is derived from the values, not hardcoded: see reviewSchema.
 */

const LABEL_COL = "252px";

/** Rows that are optional AND unanswered are hidden until asked for — a review
 *  listing every conditional the applicant never triggered buries the answers
 *  they did give. Required-but-empty always shows, as a missing answer. */
const isHidden = (resolved, revealed) =>
  !revealed && !resolved.required && isBlank(resolved.display);

const sameAnswer = (a, b) => {
  const [blankA, blankB] = [isBlank(a), isBlank(b)];
  if (blankA || blankB) return blankA && blankB;
  return isEqual(a, b);
};

const StepReview = ({ goToStep, values }) => {
  const form = useForm();
  const { initialValues } = useFormState({
    subscription: { initialValues: true },
  });

  const [editing, setEditing] = useState(() => new Set());
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [revealed, setRevealed] = useState(() => new Set());

  const toggleIn = (setState) => (key) =>
    setState((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleCollapsed = toggleIn(setCollapsed);
  const toggleRevealed = toggleIn(setRevealed);

  const openRows = useCallback(
    (paths, open) =>
      setEditing((prev) => {
        const next = new Set(prev);
        paths.forEach((p) => (open ? next.add(p) : next.delete(p)));
        return next;
      }),
    [],
  );

  const isChanged = useCallback(
    (paths) =>
      paths.some(
        (p) => !sameAnswer(get(values, p), get(initialValues, p)),
      ),
    [values, initialValues],
  );

  const undo = useCallback(
    (paths) => {
      paths.forEach((p) => form.change(p, get(initialValues, p)));
      openRows(paths, false);
    },
    [form, initialValues, openRows],
  );

  // Every visible row across every section, resolved once — the per-section
  // counters and the footer read the same list, so they can't disagree.
  const sections = useMemo(
    () => REVIEW_SECTIONS.map((section) => resolveSection(section, values)),
    [values],
  );

  const stats = useMemo(() => {
    const all = sections.flatMap((s) => s.allRows);
    return {
      missing: all.filter((r) => r.required && isBlank(r.display)).length,
      changed: all.filter((r) => isChanged(r.paths)).length,
      changedPaths: all.filter((r) => isChanged(r.paths)).flatMap((r) => r.paths),
    };
  }, [sections, isChanged]);

  const rowProps = { editing, openRows, isChanged, undo };

  return (
    <Flx column>
      <ReviewSnapshot values={values} />

      {sections.map((section) => (
        <ReviewSection
          key={section.id}
          section={section}
          values={values}
          collapsed={collapsed.has(section.id)}
          revealed={revealed.has(section.id)}
          onToggleCollapsed={() => toggleCollapsed(section.id)}
          onToggleRevealed={() => toggleRevealed(section.id)}
          onOpenStep={() => goToStep(section.step)}
          {...rowProps}
        />
      ))}

      <ReviewFooterStatus
        changed={stats.changed}
        missing={stats.missing}
        onUndoAll={() => undo(stats.changedPaths)}
      />
    </Flx>
  );
};

/* ---------------------------------------------------------------- sections */

/**
 * Resolve a section's rows against the current values. Properties are their own
 * shape — one card per entry in the `subjectProperties` array, each carrying the
 * whole per-property question set scoped to its own path.
 */
const resolveSection = (section, values) => {
  if (section.kind === "properties") {
    const properties = values?.subjectProperties || [];
    const cards = properties.map((property, index) => ({
      index,
      prefix: `subjectProperties[${index}]`,
      property,
      rows: PROPERTY_ROWS.map((row) =>
        resolveRow(row, {
          values,
          prefix: `subjectProperties[${index}]`,
          scope: property,
        }),
      ).filter((r) => r.applies),
    }));
    return {
      ...section,
      cards,
      allRows: cards.flatMap((c) => c.rows),
    };
  }

  const rows = section.rows
    .map((row) => resolveRow(row, { values }))
    .filter((r) => r.applies);

  return { ...section, rows, allRows: rows };
};

const ReviewSection = ({
  section,
  values,
  collapsed,
  revealed,
  onToggleCollapsed,
  onToggleRevealed,
  onOpenStep,
  editing,
  openRows,
  isChanged,
  undo,
}) => {
  const form = useForm();

  const visibleRows = section.allRows.filter((r) => !isHidden(r, revealed));
  const hiddenCount = section.allRows.length - visibleRows.length;
  const missing = section.allRows.filter(
    (r) => r.required && isBlank(r.display),
  ).length;
  const changed = section.allRows.filter((r) => isChanged(r.paths)).length;

  return (
    <Box
      component="section"
      sx={{
        // Sections are separated by a rule; the first one sits directly under
        // the step header, which is already a boundary.
        "&:not(:first-of-type)": {
          mt: 4,
          pt: 3.5,
          borderTop: `1px solid ${APP_TOKENS.border}`,
        },
        "&:first-of-type": { mt: 2 },
      }}
    >
      <Flx between gap={2} sx={{ alignItems: "flex-start" }}>
        <ButtonBase
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          sx={{
            flex: 1,
            minWidth: 0,
            justifyContent: "flex-start",
            gap: 1,
            borderRadius: "8px",
            px: 0.5,
            py: 0.5,
            ml: -0.5,
          }}
        >
          <ExpandMoreRounded
            sx={{
              fontSize: 20,
              color: APP_TOKENS.inkFaint,
              transform: collapsed ? "rotate(-90deg)" : "none",
              transition: "transform 180ms ease",
            }}
          />
          <Flx column gap={0.25} sx={{ minWidth: 0, textAlign: "left" }}>
            <Txt
              component="h2"
              sx={{
                m: 0,
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                color: APP_TOKENS.ink,
              }}
            >
              {section.title}
            </Txt>
            <Txt component="div" sx={{ fontSize: 12.5, color: APP_TOKENS.inkMuted }}>
              {section.sublabel}
            </Txt>
          </Flx>
        </ButtonBase>

        <Flx centerY gap={1} sx={{ flexShrink: 0, pt: 0.5 }}>
          {missing > 0 && (
            <StatusTag tone="warning">
              {`${missing} to answer`}
            </StatusTag>
          )}
          {changed > 0 && <StatusTag tone="info">{`${changed} edited`}</StatusTag>}
        </Flx>
      </Flx>

      {collapsed ? null : (
        <Box sx={{ mt: 2 }}>
          {section.kind === "properties" ? (
            <PropertyCards
              section={section}
              values={values}
              revealed={revealed}
              editing={editing}
              openRows={openRows}
              isChanged={isChanged}
              undo={undo}
              onAdd={() => form.mutators.push("subjectProperties", {})}
              onRemove={(index) =>
                form.mutators.remove("subjectProperties", index)
              }
            />
          ) : visibleRows.length === 0 ? (
            <Txt component="div" sx={{ fontSize: 13.5, color: APP_TOKENS.inkMuted }}>
              Nothing entered.
            </Txt>
          ) : (
            <Flx column gap={0}>
              {visibleRows.map((resolved) => (
                <ReviewRow
                  key={resolved.path}
                  resolved={resolved}
                  editing={editing.has(resolved.path)}
                  onOpen={() => openRows([resolved.path], true)}
                  onClose={() => openRows([resolved.path], false)}
                  changed={isChanged(resolved.paths)}
                  onUndo={() => undo(resolved.paths)}
                />
              ))}
            </Flx>
          )}

          <Flx centerY gap={2} sx={{ mt: 2 }}>
            {hiddenCount > 0 && (
              <Button variant="text" size="small" onClick={onToggleRevealed}>
                {revealed
                  ? "Hide unanswered optional questions"
                  : `Show ${hiddenCount} unanswered optional question${hiddenCount === 1 ? "" : "s"}`}
              </Button>
            )}
            {/* The step itself stays one click away: a few things (adding a
                property mid-form, re-reading a step's guidance) are still
                easier there. */}
            <Button variant="text" size="small" onClick={onOpenStep}>
              Open full step
            </Button>
          </Flx>
        </Box>
      )}
    </Box>
  );
};

/* -------------------------------------------------------------------- rows */

const ReviewRow = ({ resolved, editing, onOpen, onClose, changed, onUndo }) => {
  const { label, display, required, row, path } = resolved;
  const missing = required && isBlank(display);

  // The accent strip is the only thing distinguishing an edited row from an
  // untouched one at a glance, once the applicant has scrolled past the pill.
  const accent = missing
    ? colors.warning
    : changed
      ? colors.action
      : "transparent";

  // The whole read-mode row is clickable, so every action inside it has to stop
  // the click bubbling — otherwise Undo would immediately reopen the editor it
  // just closed.
  const stop = (fn) => (event) => {
    event.stopPropagation();
    fn();
  };

  const actions = (
    <Flx centerY gap={0.25} sx={{ justifyContent: "flex-end" }}>
      {changed && (
        <Tooltip title="Undo this change" placement="top">
          <IconButton
            size="small"
            onClick={stop(onUndo)}
            aria-label={`Undo change to ${label}`}
          >
            <UndoRounded sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={editing ? "Done" : "Edit"} placement="top">
        <IconButton
          size="small"
          onClick={stop(editing ? onClose : onOpen)}
          aria-label={`${editing ? "Finish editing" : "Edit"} ${label}`}
        >
          {editing ? (
            <CheckRounded sx={{ fontSize: 18, color: colors.action }} />
          ) : (
            <EditOutlined sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Tooltip>
    </Flx>
  );

  const shared = {
    sx: {
      position: "relative",
      display: "grid",
      alignItems: "start",
      px: 1.5,
      py: 1.25,
      borderRadius: 0,
      borderTop: `1px solid ${APP_TOKENS.border}`,
      "&:first-of-type": { borderTop: "none" },
      background: editing ? colors.slate50 : "transparent",
      transition: "background 140ms ease",
      "&:hover": { background: colors.slate50 },
      "&:hover .review-row-actions, &:focus-within .review-row-actions": {
        opacity: 1,
      },
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 6,
        bottom: 6,
        width: "3px",
        borderRadius: "99px",
        background: accent,
      },
    },
  };

  const labelNode = (
    <Txt
      component="div"
      sx={{ fontSize: 13, lineHeight: 1.45, color: APP_TOKENS.inkMuted }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: colors.danger, ml: 0.25 }}>
          *
        </Box>
      )}
    </Txt>
  );

  if (editing) {
    return (
      <Box
        {...shared}
        sx={{
          ...shared.sx,
          gridTemplateColumns: "minmax(0, 1fr) auto",
          rowGap: 1.25,
        }}
      >
        {labelNode}
        <Box className="review-row-actions" sx={{ opacity: 1 }}>
          {actions}
        </Box>
        <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
          {row.editor(path)}
        </Box>
      </Box>
    );
  }

  // A plain clickable Box, not a ButtonBase: the row contains its own buttons,
  // and the pencil is the keyboard-reachable control. Making the row itself a
  // button would nest interactive elements for no added affordance.
  return (
    <Box
      onClick={onOpen}
      {...shared}
      sx={{
        ...shared.sx,
        cursor: "pointer",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr) auto",
          md: `${LABEL_COL} minmax(0, 1fr) auto`,
        },
        columnGap: 2,
        rowGap: 0.5,
      }}
    >
      {labelNode}
      <Box sx={{ minWidth: 0, gridColumn: { xs: "1", md: "auto" } }}>
        {missing ? (
          <Flx centerY gap={0.75}>
            <WarningAmberRounded sx={{ fontSize: 16, color: colors.warningFg }} />
            <Txt
              component="div"
              sx={{ fontSize: 13.5, fontWeight: 600, color: colors.warningFg }}
            >
              Needs an answer
            </Txt>
          </Flx>
        ) : isBlank(display) ? (
          <Txt component="div" sx={{ fontSize: 13.5, color: APP_TOKENS.inkFaint }}>
            Not provided
          </Txt>
        ) : (
          <Flx centerY gap={1} sx={{ flexWrap: "wrap" }}>
            <Txt
              component="div"
              sx={{
                fontSize: 13.5,
                fontWeight: 600,
                lineHeight: 1.5,
                color: APP_TOKENS.ink,
                whiteSpace: "pre-line",
                wordBreak: "break-word",
              }}
            >
              {display}
            </Txt>
            {changed && <StatusTag tone="info">Edited</StatusTag>}
          </Flx>
        )}
      </Box>
      <Box
        className="review-row-actions"
        sx={{
          gridRow: { xs: "1 / 3", md: "auto" },
          gridColumn: { xs: "2", md: "auto" },
          opacity: { xs: 1, md: changed || missing ? 1 : 0 },
          transition: "opacity 140ms ease",
        }}
      >
        {actions}
      </Box>
    </Box>
  );
};

/* -------------------------------------------------------------- properties */

const PropertyCards = ({
  section,
  values,
  revealed,
  editing,
  openRows,
  isChanged,
  undo,
  onAdd,
  onRemove,
}) => (
  <Flx column gap={2}>
    {section.cards.length === 0 && (
      <Txt component="div" sx={{ fontSize: 13.5, color: APP_TOKENS.inkMuted }}>
        No properties added yet.
      </Txt>
    )}

    {section.cards.map((card) => {
      const visible = card.rows.filter((r) => !isHidden(r, revealed));
      const address = formatAddressObjectFullAddress(card.property?.address);

      return (
        <Box
          key={card.prefix}
          sx={{
            border: `1px solid ${APP_TOKENS.border}`,
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <Flx
            between
            centerY
            gap={1.5}
            sx={{
              px: 2,
              py: 1.25,
              background: colors.slate50,
              borderBottom: `1px solid ${APP_TOKENS.border}`,
            }}
          >
            <Flx centerY gap={1.5} sx={{ minWidth: 0 }}>
              <StatusTag tone="info">{`Property ${card.index + 1}`}</StatusTag>
              <Txt
                component="div"
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: APP_TOKENS.ink,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {address === "N/A" ? "Address not entered" : address}
              </Txt>
            </Flx>
            {/* The form is meaningless with zero properties, so removal is only
                offered while one would remain. */}
            {section.cards.length > 1 && (
              <IconButton
                size="small"
                aria-label={`Remove property ${card.index + 1}`}
                onClick={() => onRemove(card.index)}
              >
                <DeleteOutline sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Flx>

          <Box sx={{ px: 1, py: 0.5 }}>
            {visible.map((resolved) => (
              <ReviewRow
                key={resolved.path}
                resolved={resolved}
                editing={editing.has(resolved.path)}
                onOpen={() => openRows([resolved.path], true)}
                onClose={() => openRows([resolved.path], false)}
                changed={isChanged(resolved.paths)}
                onUndo={() => undo(resolved.paths)}
              />
            ))}
          </Box>

          <PropertyEconomics property={card.property} values={values} />
        </Box>
      );
    })}

    <Button variant="text" size="small" startIcon={<AddRounded />} onClick={onAdd}>
      Add another property
    </Button>
  </Flx>
);

/**
 * The one number a property card exists to produce: what's left after buying it
 * and doing the work. Renders only when both sides of the subtraction are known
 * — a "spread" computed off a missing purchase price is just the ARV again.
 */
const PropertyEconomics = ({ property, values }) => {
  const arv = num(property?.arv);
  const basis =
    num(property?.purchasePrice || property?.originalPurchasePrice || property?.asIsValue) +
    num(property?.constructionBudget);

  if (!isConstruction(values) || !arv || !basis) return null;

  return (
    <Flx
      between
      centerY
      gap={2}
      sx={{
        px: 2,
        py: 1.25,
        background: colors.slate50,
        borderTop: `1px solid ${APP_TOKENS.border}`,
      }}
    >
      <Txt component="div" sx={{ fontSize: 12.5, color: APP_TOKENS.inkMuted }}>
        After renovation value less purchase and construction
      </Txt>
      <Txt
        component="div"
        sx={{ fontSize: 13.5, fontWeight: 700, color: APP_TOKENS.ink }}
      >
        {formatDollar(arv - basis)}
      </Txt>
    </Flx>
  );
};

/* ---------------------------------------------------------------- snapshot */

const num = (v) => {
  const parsed = Number(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const pct = (part, whole) => (whole > 0 ? (part / whole) * 100 : null);

/**
 * The deal in three numbers, recomputed live as the rows below are edited.
 *
 * Every figure is arithmetic on what the applicant typed — no program limits,
 * eligibility or pricing are implied here, because none of that is decided at
 * application time and showing a threshold would read as one.
 */
const ReviewSnapshot = ({ values }) => {
  const construction = isConstruction(values);
  const properties = values?.subjectProperties || [];

  const base = num(values?.baseLoanAmount);
  const draws = construction ? num(values?.totalHoldback) : 0;
  const totalLoan = base + draws;

  const acquisition = properties.reduce(
    (sum, p) =>
      sum + num(p?.purchasePrice || p?.originalPurchasePrice || p?.asIsValue),
    0,
  );
  const constructionCost =
    num(values?.totalConstructionBudget) ||
    properties.reduce((sum, p) => sum + num(p?.constructionBudget), 0);
  const totalCost = acquisition + constructionCost;
  const arvTotal = properties.reduce((sum, p) => sum + num(p?.arv), 0);

  if (!totalLoan) return null;

  const ltc = pct(totalLoan, totalCost);
  const ltarv = pct(totalLoan, arvTotal);
  const ownCapital = totalCost - totalLoan;

  return (
    <Box
      sx={{
        border: `1px solid ${APP_TOKENS.border}`,
        borderRadius: APP_TOKENS.radius,
        background: colors.slate50,
        overflow: "hidden",
        mb: 2.5,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" },
        }}
      >
        <Metric
          label={construction ? "Total loan request" : "Loan amount"}
          value={formatDollar(totalLoan)}
          sub={
            construction && draws
              ? `${formatDollar(base)} at closing · ${formatDollar(draws)} in draws`
              : [values?.loanProductType, values?.loanPurpose]
                  .filter(Boolean)
                  .join(" · ")
          }
        />
        <Metric
          label="Total project cost"
          value={totalCost ? formatDollar(totalCost) : "—"}
          sub={
            constructionCost ? "Acquisition + construction" : "Acquisition basis"
          }
        />
        <Metric
          label="Loan to cost"
          value={ltc === null ? "—" : `${ltc.toFixed(1)}%`}
          sub={
            ltc !== null && ownCapital > 0
              ? `${formatDollar(ownCapital)} of your own capital`
              : undefined
          }
          spanFull
        />
      </Box>

      {ltarv !== null && (
        <Box sx={{ px: 2, py: 1.75, borderTop: `1px solid ${APP_TOKENS.border}` }}>
          <Flx between centerY gap={2} sx={{ mb: 1 }}>
            <Txt component="div" sx={{ fontSize: 12.5, fontWeight: 600, color: APP_TOKENS.ink }}>
              Loan against after-renovation value
            </Txt>
            <Txt component="div" sx={{ fontSize: 12.5, color: APP_TOKENS.inkMuted }}>
              {`${ltarv.toFixed(1)}% of ${formatDollar(arvTotal)}`}
            </Txt>
          </Flx>
          <Box
            sx={{
              height: 7,
              borderRadius: "99px",
              background: colors.slate200,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${Math.min(ltarv, 100)}%`,
                borderRadius: "99px",
                background: colors.action,
                transition: "width 300ms ease",
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

const Metric = ({ label, value, sub, spanFull }) => (
  <Flx
    column
    gap={0.5}
    sx={{
      px: 2,
      py: 1.75,
      borderRight: { md: `1px solid ${APP_TOKENS.border}` },
      "&:last-of-type": { borderRight: "none" },
      gridColumn: spanFull ? { xs: "1 / -1", md: "auto" } : undefined,
      borderTop: spanFull ? { xs: `1px solid ${APP_TOKENS.border}`, md: "none" } : undefined,
    }}
  >
    <Txt
      component="div"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: APP_TOKENS.inkFaint,
      }}
    >
      {label}
    </Txt>
    <Txt
      component="div"
      sx={{
        fontSize: 20,
        fontWeight: 700,
        letterSpacing: "-0.02em",
        color: APP_TOKENS.ink,
      }}
    >
      {value}
    </Txt>
    {sub && (
      <Txt component="div" sx={{ fontSize: 12, color: APP_TOKENS.inkMuted }}>
        {sub}
      </Txt>
    )}
  </Flx>
);

/* ------------------------------------------------------------ chrome bits */

const StatusTag = ({ tone = "info", children }) => {
  const palette = {
    info: { bg: colors.blue100, fg: colors.blue700 },
    warning: { bg: colors.warningBg, fg: colors.warningFg },
  }[tone];

  return (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        px: 0.9,
        py: "2px",
        borderRadius: "99px",
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.5,
        background: palette.bg,
        color: palette.fg,
      }}
    >
      {children}
    </Box>
  );
};

const ReviewFooterStatus = ({ changed, missing, onUndoAll }) => {
  if (!changed && !missing) return null;

  return (
    <Flx
      between
      centerY
      gap={2}
      sx={{
        mt: 3.5,
        px: 2,
        py: 1.5,
        borderRadius: APP_TOKENS.radius,
        background: colors.slate50,
        border: `1px solid ${APP_TOKENS.border}`,
      }}
    >
      <Txt component="div" sx={{ fontSize: 13, color: APP_TOKENS.inkMuted }}>
        {[
          changed
            ? `${changed} ${changed === 1 ? "answer" : "answers"} changed on this page`
            : null,
          missing ? `${missing} still to answer` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </Txt>
      {changed > 0 && (
        <Button variant="text" size="small" onClick={onUndoAll} sx={{ flexShrink: 0 }}>
          Undo all changes
        </Button>
      )}
    </Flx>
  );
};

export default StepReview;
